/**
 * Backend messages from POST /api/Student/add-course-by-code
 * (StudentsService.AddCourseByCodeAsync)
 */
export const COURSE_ENROLL_BY_CODE_MESSAGES = {
  success: 'Course added successfully.',
  successDefault: 'Operation succeeded.',
  codeNotFound: 'Code not found.',
  codeAlreadyUsed: 'Code has already been used.',
  studentNotFound: 'Student not found.',
  courseNotFound: 'Course not found.',
  alreadyEnrolled: 'Student already enrolled in this course.',
} as const;

export type CourseEnrollUiKind =
  | 'success'
  | 'code_not_found'
  | 'code_already_used'
  | 'already_enrolled'
  | 'student_not_found'
  | 'course_not_found'
  | 'empty_code'
  | 'not_authenticated'
  | 'network_error'
  | 'unknown';

export interface CourseEnrollUiResult {
  kind: CourseEnrollUiKind;
  success: boolean;
  /** Egyptian colloquial title */
  title: string;
  /** Egyptian colloquial body */
  message: string;
  /** Primary button label */
  buttonLabel: string;
}

const normalize = (msg: string) => msg.trim().replace(/\s+/g, ' ');

/**
 * Map backend / client enrollment responses to Egyptian Arabic popup copy.
 */
export function mapCourseEnrollByCodeResponse(input: {
  success?: boolean;
  message?: string | null;
  data?: unknown;
}): CourseEnrollUiResult {
  const rawMessage = normalize(String(input.message ?? ''));
  const dataMessage =
    typeof input.data === 'string' ? normalize(input.data) : '';

  // Backend SuccessResponse("Course added successfully.") puts the string in `data`
  // and leaves Message as default "Operation succeeded."
  const isSuccessFlag = input.success === true;
  const looksLikeSuccess =
    isSuccessFlag ||
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.success ||
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.successDefault ||
    dataMessage === COURSE_ENROLL_BY_CODE_MESSAGES.success;

  if (looksLikeSuccess && input.success !== false) {
    return {
      kind: 'success',
      success: true,
      title: 'تمام يا بطل! 🎉',
      message: 'الكود اتفَعّل والكورس بقى بتاعك. يلا نبدأ نتعلم!',
      buttonLabel: 'يلا نبدأ',
    };
  }

  const combined = `${rawMessage} ${dataMessage}`.toLowerCase();

  if (
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.codeNotFound ||
    combined.includes('code not found')
  ) {
    return {
      kind: 'code_not_found',
      success: false,
      title: 'الكود مش موجود 😅',
      message: 'الكود اللي كتبته مش متسجل عندنا. بص عليه كويس وحاول تاني.',
      buttonLabel: 'تمام يا كبير',
    };
  }

  if (
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.codeAlreadyUsed ||
    combined.includes('already been used')
  ) {
    return {
      kind: 'code_already_used',
      success: false,
      title: 'الكود ده اتستخدم قبل كده 🔒',
      message: 'الكود اتحرق خلاص ومش هينفع يتفعل تاني. لو في مشكلة كلّم الإشراف.',
      buttonLabel: 'حاضر',
    };
  }

  if (
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.alreadyEnrolled ||
    combined.includes('already enrolled')
  ) {
    return {
      kind: 'already_enrolled',
      success: false,
      title: 'إنت مشترك أصلاً! ✅',
      message: 'إنت مسجّل في الكورس ده من قبل. روح لكورساتك وكمّل من حيث ما وقفت.',
      buttonLabel: 'روح لكورساتي',
    };
  }

  if (
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.studentNotFound ||
    combined.includes('student not found')
  ) {
    return {
      kind: 'student_not_found',
      success: false,
      title: 'في مشكلة في الحساب 🤔',
      message: 'مفيش حساب طالب ظاهر دلوقتي. اعمل خروج ودخول تاني وبعدين حاول.',
      buttonLabel: 'تمام',
    };
  }

  if (
    rawMessage === COURSE_ENROLL_BY_CODE_MESSAGES.courseNotFound ||
    combined.includes('course not found')
  ) {
    return {
      kind: 'course_not_found',
      success: false,
      title: 'الكورس مش ظاهر 😕',
      message: 'الكورس اللي مربوط بالكود مش موجود دلوقتي. كلّم الإشراف يساعدوك.',
      buttonLabel: 'حاضر',
    };
  }

  if (
    rawMessage === 'User not authenticated' ||
    combined.includes('not authenticated') ||
    combined.includes('please log in')
  ) {
    return {
      kind: 'not_authenticated',
      success: false,
      title: 'سجّل دخول الأول 🔐',
      message: 'لازم تكون مسجّل دخول عشان تفعّل الكود وتدخل الكورس.',
      buttonLabel: 'روح للدخول',
    };
  }

  if (
    combined.includes('an error occurred') ||
    combined.includes('failed to enroll') ||
    combined.includes('network')
  ) {
    return {
      kind: 'network_error',
      success: false,
      title: 'حصل خطأ بسيط ⚠️',
      message: 'النِت ممكن يكون ضعيف. تأكد من الاتصال وحاول مرة كمان.',
      buttonLabel: 'حاضر',
    };
  }

  if (!rawMessage && !dataMessage) {
    return {
      kind: 'unknown',
      success: false,
      title: 'مقدرناش نكمّل 😕',
      message: 'حاجة حصلت ومكملتش. حاول تاني أو كلّم الإشراف.',
      buttonLabel: 'تمام',
    };
  }

  return {
    kind: 'unknown',
    success: false,
    title: 'مقدرناش نفعّل الكود',
    message: rawMessage || dataMessage || 'حاول تاني ولو المشكلة فضلت كلّم الإشراف.',
    buttonLabel: 'تمام',
  };
}

export function emptyCodeEnrollResult(): CourseEnrollUiResult {
  return {
    kind: 'empty_code',
    success: false,
    title: 'اكتب الكود يا بطل ✍️',
    message: 'لسه مدخلتش الكود. اكتبه الأول وبعدين اضغط تفعيل.',
    buttonLabel: 'حاضر',
  };
}
